import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import { createThunkRequest, DELETE, POST } from '@webapp/account/src/libs/request';

import { DATETIME_SERVER_FORMAT, UI_DATETIME_FORMAT } from '@webapp/common/lib/date';

import { patchObject } from '../lib';
import { initialLicense, mapLicense, mapQuotas } from '../profile';
import { selectCurrentSurveyId, selectSmsing } from '../survey/selectors';

import type { SmsFilters } from './filters';
import { mapGroup, mapRecipient, mapSms, reverseMapSms } from './map';
import type { Recipient } from './recipient';
import { selectCurrentSms, selectCurrentSmsId } from './selectors';
import type { Sms } from './smsing';
import { initialSms, RecipientType, SmsType } from './smsing';

export const getSmsList = createThunkRequest(
    'smsing/getSmsList',
    async ({ surveyId }: { surveyId: number }) => {
        const { lists } = await POST('show/sms_lists', { surveyId });

        return {
            items: lists.map(mapSms),
        };
    },
    { notify: false },
);

export const smsStatDownloadCSV = createThunkRequest(
    'smsing/smsStatDownloadCSV',
    async ({ id = 1, filters }: { id: number; filters: SmsFilters }) => {
        const { file } = await POST('get/sms_csv_stat', {
            surveyId: id,
            status: filters.status,
        });

        return { file };
    },
    { notify: false },
);

export const getSmsGroups = createThunkRequest(
    'smsing/getSmsGroups',
    async () => {
        const { rows } = await POST('show/sms_groups', {
            excludeHidden: 1,
        });

        return {
            smsGroups: rows.map(mapGroup),
        };
    },
    { notify: false },
);

export const sendCurrentSms = createThunkRequest(
    'smsing/sendCurrentSms',

    async (_, { getState }) => {
        const state = getState();
        const { id, sendDatetime, sendNow } = selectCurrentSms(state);
        const surveyId = selectCurrentSurveyId(state);
        const datetime = sendNow ? undefined : sendDatetime;

        const req: any = {
            listId: id,
        };

        if (datetime && !sendNow) {
            req.rundate = datetime.format(DATETIME_SERVER_FORMAT);
        }

        await POST('run/sms_list', req);

        const message = sendNow
            ? 'Рассылка будет готова для отправки в течении 1 минуты'
            : `Рассылка будет отправлена ${dayjs(datetime).format(UI_DATETIME_FORMAT)}`;

        return {
            message,
            surveyId,
        };
    },
);

export const addExistingSmsGroup = createThunkRequest(
    'smsing/addExistingSmsGroup',
    async ({ groupId, linkParams }: { groupId: number; linkParams?: { [key: string]: string } }, { getState }) => {
        const list = selectCurrentSmsId(getState());
        const { message } = await POST('add/phones_to_list', {
            listId: list,
            groupId,
            linkParams,
        });
        return {
            message,
            groupId,
        };
    },
);

export const removeSmsGroupFromList = createThunkRequest(
    'smsing/removeSmsGroupFromSmsing',
    async ({ listId, groups }: { listId: number; groups: Array<number> }) => {
        const { message } = await POST('delete/phones_from_list', {
            listId,
            groupIds: groups,
        });

        return {
            message,
            groups,
        };
    },
);

export const getSmsQuotasGetByDate = createThunkRequest(
    'smsing/getSmsQuotasGetByDate',
    async ({ date, sendNow }: { date: string; sendNow: boolean }) => {
        const { license, surveys } = await POST('show/quotas', sendNow ? {} : { date });

        return {
            license: {
                ...cloneDeep(initialLicense),
                ...mapLicense(license),
                surveys: mapQuotas(surveys),
            },
        };
    },
    { notify: false },
);

export const loadSmsing = createThunkRequest(
    'smsing/loadSmsing',
    async ({ surveyId, listId }: { surveyId: number; listId: number }) => {
        const result = await POST('show/sms_list', {
            surveyId,
            listId,
        });

        const { groups, list, recipients, duplicate, recipient_count, tags } = result;
        const item = mapSms({ ...list, duplicate });

        item.recipients = recipients.map(mapRecipient);
        item.smsGroups = groups.map(mapGroup);
        item.stat = {
            all: recipient_count.all,
            answered: recipient_count.answered,
            notAnswered: recipient_count.not_answered,
        };
        item.tags = tags;

        return {
            item,
        };
    },
    { notify: false },
);

export const addRecipient = createThunkRequest(
    'smsing/addRecipient',
    async ({ listId, recipient }: { listId: number; recipient: string }) => {
        const data = await POST('add/sms_recipient', {
            listId,
            recipient,
        });

        const { id, message } = data;

        return {
            recipient,
            message,
            id,
        };
    },
);

export const changeSmsGroupName = createThunkRequest(
    'smsing/changeSmsGroupName',
    async ({ groupId, name }: { groupId: number; name: string }) => {
        const { message } = await POST('change/sms_group', {
            id: groupId,
            name,
        });
        return {
            message,
            groupId,
            name,
        };
    },
);

export const deleteSmsGroup = createThunkRequest(
    'smsing/deleteSmsGroup',
    async ({ groups }: { groups: Array<number> }) => {
        const { message } = await POST('delete/sms_group', {
            groups,
        });

        return {
            message,
            groups,
        };
    },
);

export const addRecipientsFromCSV = createThunkRequest(
    'smsing/addRecipientsFromCSV',
    async ({ file, listId }: { file: File; listId: number }) => {
        let { group } = await POST('upload/sms/file', {
            upfile: file,
            listId,
        });

        group = mapGroup(group);

        return {
            group,
        };
    },
);

export const removeRecipient = createThunkRequest(
    'smsing/removeRecipient',
    async ({ recipients, listId }: { recipients: Array<Recipient>; listId: number | string }) => {
        const ids = recipients.map(({ id }) => id);
        const { message } = await POST('delete/sms_recipient', {
            recIds: JSON.stringify(ids),
            listId,
        });

        return {
            message,
            ids,
        };
    },
);

export const changeSms = createThunkRequest(
    'smsing/changeSms',
    async ({ id, change }: { id: number; change?: Partial<Sms> }) => {
        const updatedParams = reverseMapSms(change);

        const { message } = await POST('change/sms_list', {
            listId: id,
            ...updatedParams,
        });

        return {
            id,
            message,
            change,
        };
    },
);

export const addSms = createThunkRequest(
    'smsing/addSms',
    async ({ surveyId, smsType }: { surveyId: number; smsType: SmsType }, { getState }) => {
        const smsing = selectSmsing(getState());
        const sms = cloneDeep(initialSms);

        const req: AnyObject = {
            surveyId,
            reminder: smsType === SmsType.NOTIFY ? 1 : 0,
            identification: sms.bindSms ? 1 : 0,
            who: smsType === SmsType.NOTIFY ? RecipientType.NO_REPLY : null,
        };

        req.template = JSON.stringify(sms.template);

        if (smsing) {
            req.params = JSON.stringify(smsing);
        } else {
            req.params = JSON.stringify({ disallowRefilling: true });
        }

        const { list: item, duplicate, message } = await POST('add/sms_list', req as any);

        return {
            surveyId,
            sms: mapSms({ ...item, duplicate }),
            title: req.title,
            message,
        };
    },
);

export const addSmsGroup = createThunkRequest(
    'smsing/addSmsGroup',
    async (
        {
            phones,
            name,
            hidden,
        }: {
            phones: Array<string>;
            name: string;
            hidden: boolean;
        },
        { getState },
    ) => {
        const listId = selectCurrentSmsId(getState());

        const { id, message } = await POST('add/sms_group', {
            name,
            data: JSON.stringify(phones),
            hidden: hidden ? 1 : 0,
            listId,
        });

        return {
            newGroupId: id,
            message,
            phones,
            name,
            hidden: 0,
        };
    },
);

export const changeSmsParam = createThunkRequest(
    'smsing/changeSmsParam',
    async (
        { listId, param, value }: { listId: number; param: string; value?: string | number | boolean },
        { getState },
    ) => {
        const sms: Sms = selectCurrentSms(getState());

        const params = cloneDeep(sms.params);

        patchObject(params, param, value);

        if (param === 'excludeDuplicate' && !params.excludeDuplicateWho) {
            params.excludeDuplicateWho = ['ALL_NO_DELETE'];
        }

        const req: JsonValue = {
            listId,
            params: JSON.stringify(params),
        };

        const { message, duplicate } = await POST('change/sms_list', req);

        return { message, params, duplicate };
    },
);

export const logoutSms = createThunkRequest(
    'smsing/logout',

    async () => {
        return await DELETE('delete/sms_settings');
    },
);
